import type { SxProps, Theme } from "@mui/material";
import { Typography } from "@mui/material";
import { isEmptyObject } from "@entrilia/shared/utilities/objectHelper.ts";
import type { Report, ReportField, ReportTemplate } from "../../../../../api/types/svTypes.ts";
import { getDisplayCaption } from "../components/viewer/common/fields/field-captions/captionsHelper.ts";

export function getFieldCaption(field: ReportField): string;
export function getFieldCaption(
  field: { config: { customLabel?: string; systemLabel?: string }; meta: { caption: string } } | undefined
): string;
export function getFieldCaption(
  field: ReportField | { config: { customLabel?: string; systemLabel?: string }; meta: { caption: string } } | undefined
): string {
  if (field === undefined) {
    return "No Name";
  }
  const systemLabel = "systemLabel" in field.config ? (field.config.systemLabel as string | undefined) : undefined;
  return getDisplayCaption(field.meta.caption, field.config.customLabel, systemLabel);
}

export const styleCaption = (field: ReportField, value: string | undefined) => {
  if (!field.config.style?.fontStyle) {
    return <Typography component={"span"}>{value}</Typography>;
  }

  return (
    <Typography
      component="span"
      sx={{
        fontWeight: field.config.style.fontStyle.indexOf("bold") > -1 ? "bold" : "inherit",
        textDecoration: field.config.style.fontStyle.indexOf("underlined") > -1 ? "underline" : "inherit",
        fontStyle: field.config.style.fontStyle.indexOf("italic") > -1 ? "italic" : "inherit",
      }}
    >
      {value}
    </Typography>
  );
};

export function insertItemAt<T>(array: T[], item: T, index: number): T[] {
  if (index < 0 || index > array.length) {
    throw new Error("Index is out of range");
  }

  return [...array.slice(0, index), item, ...array.slice(index)];
}

export function areAllFieldsEmpty(obj: Record<string, unknown>): boolean {
  return Object.values(obj).every((value) => {
    if (Array.isArray(value)) {
      return value.length === 0;
    } else if (typeof value === "object" && value !== null) {
      return isEmptyObject(value);
    }
    return false;
  });
}

export const isNewReport = (report: Report | ReportTemplate) => {
  return report.reportId === undefined;
};

export const getBiDataGridStyles: SxProps<Theme> = (t: Theme) => ({
  "--DataGrid-rowBorderColor": t.palette.divider,
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "divider",
  ".MuiDataGrid-virtualScroller--hasScrollX .MuiDataGrid-columnHeader--last": {
    borderRight: "1px solid var(--DataGrid-rowBorderColor)",
  },
  ".MuiDataGrid-columnHeaders": {
    ".MuiDataGrid-columnHeaderTitleContainer": {
      ".MuiDataGrid-iconButtonContainer": {
        ".MuiBadge-badge": {
          display: "none",
        },
      },
    },
    ".MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainer": {
      borderBottom: "none",
    },
    ".MuiDataGrid-columnHeader--filledGroup": {
      borderBottom: "1px solid var(--DataGrid-rowBorderColor)",
    },
  },
  ".MuiDataGrid-columnSeparator": {
    color: t.palette.divider,
    "&.MuiDataGrid-columnSeparator--resizing": {
      color: t.palette.divider,
    },
  },
  ".MuiDataGrid-pinnedRows--bottom": {
    ".MuiDataGrid-cell--pinnedLeft": {
      background: "inherit",
    },
  },
  ".MuiDataGrid-container--bottom [role=row]": {
    background: t.palette.background.grey,
  },
  ".MuiDataGrid-scrollbarFiller": {
    "&.MuiDataGrid-scrollbarFiller--borderTop": {
      borderTop: "none",
    },
  },

  "[data-id^=auto-generated-group-footer][role=row] .MuiDataGrid-cell--pinnedLeft": {
    backgroundColor: t.palette.background.grey,
  },
  ".MuiDataGrid-virtualScrollerContent [data-id^=auto-generated-group-footer][role=row]:hover .MuiDataGrid-cell--pinnedLeft":
    {
      backgroundColor: "inherit",
    },
});
